import React, { useEffect, useRef, useState } from "react";
import { Html5Qrcode, Html5QrcodeSupportedFormats } from "html5-qrcode";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Camera, X, ScanLine, RotateCw } from "lucide-react";

export default function BarcodeScanner({ open, onClose, onDetected }) {
  const [error, setError] = useState(null);
  const [running, setRunning] = useState(false);
  const [retryToken, setRetryToken] = useState(0);
  const scannerRef = useRef(null);
  const containerId = "barcode-scanner-region";

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    let html5 = null;
    setError(null);
    setRunning(false);

    const start = async () => {
      try {
        // Phase 87bd — Vérif env (mediaDevices requis, HTTPS sinon Firefox/Brave bloquent silencieusement)
        if (!navigator.mediaDevices?.getUserMedia) {
          throw new Error("Le navigateur ne supporte pas l'accès caméra. Utilisez Firefox, Brave ou Chromium à jour en HTTPS.");
        }

        // Phase 87bd — Force la prompt permission AVANT html5-qrcode
        // (sur Firefox/Brave Android, html5-qrcode ne demande PAS de prompt si pas pré-autorisé)
        try {
          const probe = await navigator.mediaDevices.getUserMedia({ video: { facingMode: "environment" } });
          probe.getTracks().forEach((t) => t.stop());
        } catch (permErr) {
          throw permErr; // bubble up vers humanizeCameraError
        }

        if (cancelled) return;

        // Phase 87at — Support explicite EAN13 + QR + Code128 + DataMatrix etc.
        const formats = [
          Html5QrcodeSupportedFormats.QR_CODE,
          Html5QrcodeSupportedFormats.EAN_13,
          Html5QrcodeSupportedFormats.EAN_8,
          Html5QrcodeSupportedFormats.CODE_128,
          Html5QrcodeSupportedFormats.CODE_39,
          Html5QrcodeSupportedFormats.UPC_A,
          Html5QrcodeSupportedFormats.UPC_E,
          Html5QrcodeSupportedFormats.DATA_MATRIX,
          Html5QrcodeSupportedFormats.ITF,
        ];
        html5 = new Html5Qrcode(containerId, {
          verbose: false,
          formatsToSupport: formats,
          // Phase 87at — Active les BarcodeDetector natifs si disponibles (perfs ++ sur Android)
          useBarCodeDetectorIfSupported: true,
        });
        scannerRef.current = html5;
        // Phase 87at — Cadre carré (240x240) qui convient à la fois QR et code-barres 1D
        await html5.start(
          { facingMode: "environment" },
          {
            fps: 15,
            qrbox: { width: 240, height: 240 },
          },
          (decoded) => {
            if (cancelled) return;
            cancelled = true;
            onDetected?.(decoded);
            html5.stop().then(() => html5.clear()).catch(() => {});
          },
          () => {}
        );
        if (!cancelled) setRunning(true);

        // Phase 101gf (Feb 2026) — Garde-fou tablette : certains navigateurs
        // Android passent la vidéo caméra en plein écran automatique dès
        // qu'elle démarre. On force les attributs `playsinline` /
        // `webkit-playsinline` et on désactive les contrôles natifs qui
        // peuvent proposer "Entrer en plein écran".
        try {
          const container = document.getElementById(containerId);
          const vid = container?.querySelector("video");
          if (vid) {
            vid.setAttribute("playsinline", "true");
            vid.setAttribute("webkit-playsinline", "true");
            vid.setAttribute("x-webkit-airplay", "deny");
            vid.removeAttribute("controls");
            vid.style.maxHeight = "60vh";
            vid.style.objectFit = "cover";
          }
        } catch (_e) { /* non bloquant */ }
      } catch (e) {
        setError(humanizeCameraError(e));
      }
    };
    start();

    return () => {
      cancelled = true;
      const s = scannerRef.current;
      if (s) {
        s.stop().then(() => s.clear()).catch(() => {});
      }
      setRunning(false);
    };
  }, [open, onDetected, retryToken]);

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose?.()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <ScanLine className="w-5 h-5 text-[#1F4E3D]" /> Scanner un code-barres ou QR
          </DialogTitle>
        </DialogHeader>
        <div className="space-y-3 py-2">
          {error ? (
            <div className="bg-[#FCEEEA] border border-[#E8C1B4] text-[#C05C47] rounded-md p-4 text-sm" data-testid="camera-error-banner">
              <div className="font-semibold mb-1.5">Caméra inaccessible</div>
              <div className="leading-relaxed text-[13px] whitespace-pre-wrap">{error}</div>
              <Button
                onClick={() => setRetryToken((t) => t + 1)}
                className="mt-3 bg-[#C1A063] hover:bg-[#A88747] text-black"
                size="sm"
                data-testid="camera-retry-btn"
              >
                <RotateCw className="w-4 h-4 mr-1.5" /> Réessayer
              </Button>
            </div>
          ) : (
            <>
              <div id={containerId} className="w-full rounded-md overflow-hidden bg-black min-h-[320px]" data-testid="scanner-video" />
              <div className="text-xs text-[#575A57] text-center flex items-center gap-2 justify-center">
                <Camera className="w-3.5 h-3.5" />
                {running ? "Placez le code-barres EAN13 OU le QR Code dans le cadre…" : "Démarrage de la caméra…"}
              </div>
            </>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} data-testid="scanner-close">
            <X className="w-4 h-4 mr-2" /> Fermer
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// Phase 87bd — Messages d'erreur humanisés (cause #1 sur Firefox/Brave Android = NotAllowedError)
function humanizeCameraError(e) {
  const msg = e?.message || String(e || "");
  const name = e?.name || "";
  if (name === "NotAllowedError" || /permission|denied|notallowed/i.test(msg)) {
    return (
      "Permission caméra refusée par le navigateur.\n\n" +
      "• Firefox Android : icône cadenas (barre d'adresse) → Permissions → Caméra → Autoriser.\n" +
      "• Brave Android : icône lion (Shields) → désactiver pour ce site, puis cadenas → Caméra → Autoriser.\n" +
      "• Sinon : paramètres du navigateur → Sites → Caméra → autorisez maison-thai.ch."
    );
  }
  if (name === "NotFoundError" || /notfound|no.+camera/i.test(msg)) {
    return "Aucune caméra disponible. Vérifiez qu'elle n'est pas désactivée dans les paramètres système ou utilisée par une autre app.";
  }
  if (name === "NotReadableError" || /inuse|in use|notreadable/i.test(msg)) {
    return "La caméra est utilisée par une autre application. Fermez-la (visio, scan, etc.) puis Réessayez.";
  }
  if (name === "SecurityError" || /secure|https/i.test(msg)) {
    return "Le navigateur exige HTTPS pour accéder à la caméra. Basculez sur https://maison-thai.ch (avec le S).";
  }
  if (name === "OverconstrainedError" || /constraint/i.test(msg)) {
    return "La résolution demandée n'est pas supportée par cette caméra. Réessayez avec la caméra avant.";
  }
  return msg.slice(0, 250) || "Erreur d'accès caméra inconnue. Réessayez ou utilisez le lecteur USB.";
}
